import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AdminLayout from '../../components/AdminLayout';
import StatCards from '../../components/StatCards';
import { getAllEnquiries, updateEnquiryStatus, deleteEnquiry } from '../../services/api';

const ITEMS_PER_PAGE = 10;

/* â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'â€”';

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'â€”';

const normalizeStatus = (s) => (s || 'new').toLowerCase();

const statusMeta = {
  new: { cls: 'bdg-b', label: 'New' },
  contacted: { cls: 'bdg-y', label: 'Contacted' },
  closed: { cls: 'bdg-g', label: 'Closed' },
};
const getStatus = (s) => statusMeta[normalizeStatus(s)] || { cls: 'bdg-b', label: s || 'New' };

const isEnquiryActive = (e) => !e?.isDeleted;

const pgBtn = (disabled, active) => ({
  minWidth: 34, height: 34, padding: '0 10px', borderRadius: 8,
  border: active ? '1px solid #ff4da6' : '1px solid rgba(255,255,255,0.12)',
  background: active ? 'rgba(255,77,166,0.2)' : disabled ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)',
  color: active ? '#ff4da6' : disabled ? '#64748b' : '#e2e8f0',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 13, fontWeight: active ? 700 : 500,
  fontFamily: "'Sora','Outfit',sans-serif", transition: 'all .15s',
  opacity: disabled ? 0.5 : 1,
});

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);
  const getPageNumbers = () => {
    const pages = [], maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 4px 4px', borderTop: '1px solid rgba(14,165,233,0.12)', marginTop: 8, flexWrap: 'wrap', gap: 10 }}>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>Showing {startItem}â€“{endItem} of {totalItems} enquiries</span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button onClick={() => onPageChange(1)} disabled={currentPage === 1} style={pgBtn(currentPage === 1)}>Â«</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={pgBtn(currentPage === 1)}>â€¹</button>
        {getPageNumbers().map(p => <button key={p} onClick={() => onPageChange(p)} style={pgBtn(false, p === currentPage)}>{p}</button>)}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={pgBtn(currentPage === totalPages)}>â€º</button>
        <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} style={pgBtn(currentPage === totalPages)}>Â»</button>
      </div>
    </div>
  );
};

const IconView = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const IconDelete = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
  </svg>
);

const PORTAL_OVERLAY_STYLE = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(13,6,40,0.75)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 99999,
  padding: 24,
};

const PortalModal = ({ onBackdropClick, children }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div
      style={PORTAL_OVERLAY_STYLE}
      onMouseDown={(e) => { if (e.target === e.currentTarget && onBackdropClick) onBackdropClick(); }}
    >
      {children}
    </div>,
    document.body
  );
};

const PortaledDropdown = ({ top, right, onClose, onView, onDelete }) => {
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.act-menu-dd-portal')) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return createPortal(
    <div
      className="act-menu-dd-portal"
      style={{
        position: 'fixed',
        top,
        right,
        minWidth: 162,
        background: '#20105E',
        border: '1px solid rgba(62,124,140,0.28)',
        borderRadius: 12,
        boxShadow: '0 16px 40px rgba(123,44,255,0.2), 0 4px 12px rgba(0,0,0,0.15)',
        padding: 6,
        zIndex: 999999,
      }}
    >
      <button type="button" className="act-menu-item" onClick={() => { onClose(); onView(); }}>
        <span className="act-menu-icon"><IconView /></span>View
      </button>
      <button type="button" className="act-menu-item act-menu-delete" onClick={() => { onClose(); onDelete(); }}>
        <span className="act-menu-icon"><IconDelete /></span>Delete
      </button>
    </div>,
    document.body
  );
};

const DeleteConfirmModal = ({ itemName, onConfirm, onCancel }) => (
  <PortalModal onBackdropClick={onCancel}>
    <div style={{ width: '100%', maxWidth: '520px', borderRadius: 24, background: 'linear-gradient(180deg,#f8fbff,#eff6ff)', border: '1px solid rgba(14,165,233,.18)', boxShadow: '0 40px 80px rgba(14,165,233,.18)', padding: '32px', color: '#0f172a', textAlign: 'center' }}>
      <div style={{ width: 60, height: 60, margin: '0 auto 20px', borderRadius: '50%', background: 'rgba(14,165,233,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: '#0ea5e9' }}>!</div>
      <h2 style={{ color: '#0f766e', fontSize: 26, fontWeight: 800, margin: '0 0 8px' }}>Confirm delete?</h2>
      <p style={{ color: '#334155', fontSize: 15, margin: '0 0 20px', fontWeight: 500 }}>This action cannot be undone.</p>
      <div style={{ display: 'flex', width: 'fit-content', margin: '0 auto 24px', padding: '12px 22px', borderRadius: 999, border: '1px solid rgba(14,165,233,.34)', background: 'rgba(224,242,254,.86)', color: '#0f766e', fontWeight: 800, fontSize: 15, textAlign: 'center', justifyContent: 'center' }}>{itemName || 'selected enquiry'}</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 14 }}>
        <button onClick={onCancel} style={{ padding: '12px 22px', borderRadius: 14, border: '1px solid rgba(14,165,233,.4)', background: 'rgba(224,242,254,.14)', color: '#0f766e', fontWeight: 800, cursor: 'pointer' }}>Cancel</button>
        <button onClick={onConfirm} style={{ padding: '12px 22px', borderRadius: 14, border: 'none', background: 'linear-gradient(90deg,#0ea5e9,#fb923c)', color: '#fff', fontWeight: 800, cursor: 'pointer', boxShadow: '0 10px 30px rgba(14,165,233,.2)' }}>Delete</button>
      </div>
    </div>
  </PortalModal>
);

const CustomSelect = ({ value, onChange, options, disabled, triggerClassName }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = React.useRef();
  const listRef = React.useRef();

  const openList = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (ev) => {
      if (triggerRef.current && triggerRef.current.contains(ev.target)) return;
      if (listRef.current && listRef.current.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    const reposition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: triggerClassName === 'enq-cs-filter-trigger' ? 'auto' : '100%' }}>
      <button
        type="button"
        ref={triggerRef}
        className={'enq-cs-trigger ' + (triggerClassName || '')}
        disabled={disabled}
        onClick={() => { if (disabled) return; open ? setOpen(false) : openList(); }}
      >
        <span>{selected ? selected.label : 'Select'}</span>
        <span style={{ fontSize: 11, color: '#2d5f6e', marginLeft: 8 }}>â–¼</span>
      </button>
      {open && createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
            background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
            borderRadius: 10, zIndex: 999999,
            boxShadow: '0 16px 40px rgba(15,23,42,0.16)',
          }}
        >
          {options.map(o => {
            const isSel = o.value === value;
            return (
              <div
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  padding: '9px 12px', fontSize: 13, cursor: 'pointer',
                  color: isSel ? '#ffffff' : '#102a33',
                  background: isSel ? '#3E7C8C' : '#ffffff',
                  fontFamily: "'Sora','Outfit',sans-serif",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(ev) => { if (!isSel) ev.currentTarget.style.background = '#eef8fa'; }}
                onMouseLeave={(ev) => { if (!isSel) ev.currentTarget.style.background = '#ffffff'; }}
              >
                {o.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};


const STAT_FILTER_MAP = {
  'Total Enquiries': 'all',
  'New': 'new',
  'In Progress': 'contacted',
  'Resolved': 'closed',
};

/* â”€â”€â”€ Row background tokens â”€â”€â”€ */
const ROW_ODD_BG = '#20105E';
const ROW_EVEN_BG = '#251366';
const ROW_HOVER_BG = '#2E1065';
const ROW_SELECTED_BG = '#3D2B7F';
const TH_BG = '#120A3A';

const STICKY_COL_SHADOW = '-6px 0 16px -4px rgba(14,165,233,0.20)';

const Enquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [selEnquiry, setSelEnquiry] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const [hoveredRow, setHoveredRow] = useState(null);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllEnquiries();
      const raw = res?.data?.data || res?.data?.enquiries || res?.data || [];
      const list = Array.isArray(raw) ? raw : [];
      setEnquiries(list.filter(isEnquiryActive));
    } catch (err) {
      console.error('Enquiries fetch error:', err);
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEnquiries(); }, [loadEnquiries]);
  useEffect(() => { setCurrentPage(1); }, [search, statusFilter, sortBy]);

  const countStatus = (s) => enquiries.filter(e => normalizeStatus(e.status) === s).length;

  const handleStatCardClick = (label) => {
    const filterValue = STAT_FILTER_MAP[label];
    if (filterValue === undefined) return;
    setStatusFilter(filterValue);
    setSortBy('newest');
  };

  const statsData = [
    { lbl: 'Total Enquiries', val: enquiries.length, color: '#d99a2b', activeFilter: 'all', icon: 'message', desc: 'All website requests' },
    { lbl: 'New', val: countStatus('new'), color: '#0ea5e9', activeFilter: 'new', icon: 'alert', desc: 'Fresh submissions' },
    { lbl: 'In Progress', val: countStatus('contacted'), color: '#f59e0b', activeFilter: 'contacted', icon: 'clock', desc: 'Contacted requests' },
    { lbl: 'Resolved', val: countStatus('closed'), color: '#16a34a', activeFilter: 'closed', icon: 'check', desc: 'Closed enquiries' },
  ];

  const filtered = enquiries
    .filter(e => {
      const q = search.toLowerCase();
      const matchSearch = !q
        || (e.name || '').toLowerCase().includes(q)
        || (e.institutionName || '').toLowerCase().includes(q)
        || (e.email || '').toLowerCase().includes(q)
        || (e.phone || '').toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || normalizeStatus(e.status) === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (sortBy === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      return 0;
    });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  const pageItems = filtered.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  const getRowBg = (idx, id) => {
    if (selEnquiry && selEnquiry._id === id) return ROW_SELECTED_BG;
    if (hoveredRow === id) return ROW_HOVER_BG;
    return idx % 2 === 0 ? ROW_ODD_BG : ROW_EVEN_BG;
  };

  const handleStatusChange = async (id, newStatus) => {
    const nextStatus = normalizeStatus(newStatus);
    try {
      await updateEnquiryStatus(id, { status: nextStatus });
      setEnquiries(prev => prev.map(e => e._id === id ? { ...e, status: nextStatus } : e));
      if (selEnquiry && selEnquiry._id === id) setSelEnquiry(prev => ({ ...prev, status: nextStatus }));
      setStatusMsg('âœ… Status updated!');
      window.dispatchEvent(new Event('adminDataUpdated'));
      setTimeout(() => setStatusMsg(''), 2500);
    } catch {
      setStatusMsg('âŒ Update failed');
      setTimeout(() => setStatusMsg(''), 2500);
    }
  };

  const openDelete = (enquiry) => {
    setDeleteConfirm({
      id: enquiry._id,
      label: enquiry.name ? `${enquiry.name} â€” ${enquiry.institutionName || ''}`.trim() : 'selected enquiry',
      enquiry,
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm?.id) return;
    try {
      await deleteEnquiry(deleteConfirm.id);
      setEnquiries(prev => prev.filter(e => e._id !== deleteConfirm.id));
      if (selEnquiry && selEnquiry._id === deleteConfirm.id) setSelEnquiry(null);
      setDeleteConfirm(null);
      window.dispatchEvent(new Event('adminDataUpdated'));
    } catch {
      alert('Delete failed. Please try again.');
    }
  };

  const cancelDelete = () => setDeleteConfirm(null);

  const handleMenuOpen = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    setOpenMenuId(prev => (prev === id ? null : id));
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');

    .page-shell{position:relative;padding:6px 4px 24px;font-family:'Sora','Outfit',sans-serif;color:#111827;}
    .page-stack{position:relative;z-index:1;}
    .page-header-row{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:14px;}
    .page-header-main{display:flex;flex-direction:column;gap:8px;max-width:calc(100% - 160px);}
    .page-header-title-row{display:flex;align-items:center;gap:10px;}
    .page-header-title-row h1, .enquiries-title{font-size:1.6rem;font-weight:800;color:var(--ink, #102a33) !important;margin:0 0 2px;letter-spacing:-0.2px;}
    .page-header-icon{display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:14px;background:rgba(123,44,255,0.15);color:var(--secondary);box-shadow:inset 0 0 0 1px rgba(123,44,255,0.25);animation:iconFloat 5s ease-in-out infinite;}
    @keyframes iconFloat{0%,100%{transform:translateY(0);}50%{transform:translateY(-2px);}}
    .header-subtitle{font-size:13px;color:var(--text-secondary);font-weight:600;}

    .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px;}
    @media(max-width:1200px){.stats-grid{grid-template-columns:repeat(2,1fr);}}
    .stat-card{border-radius:var(--radius-lg);padding:20px 20px 16px;border:1px solid var(--border-color);background:var(--card-bg);cursor:pointer;position:relative;overflow:hidden;transition:all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);box-shadow:0 8px 24px rgba(123, 44, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1);backdrop-filter:blur(10px);}
    .stat-card-clickable{cursor:pointer;}
    .stat-card-clickable:hover{transform:translateY(-8px) scale(1.015);box-shadow:0 16px 40px rgba(123, 44, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.15);border-color:rgba(123, 44, 255, 0.3);background:var(--card-bg-hover);}
    .stat-card-active{border-width:2px !important;box-shadow:0 0 0 3px rgba(123, 44, 255, 0.1), 0 16px 40px rgba(123, 44, 255, 0.25) !important;background:var(--card-bg-hover);}
    .stat-val{font-size:1.4rem;font-weight:800;line-height:1;margin-bottom:5px;color:var(--text-primary);}
    .stat-lbl{font-size:11px;color:var(--text-secondary);font-weight:500;}
    .stat-active-dot{display:inline-block;width:6px;height:6px;border-radius:50%;margin-left:6px;vertical-align:middle;animation:pulse-dot 1.4s ease-in-out infinite;}
    @keyframes pulse-dot{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.5;transform:scale(.75);}}

    .wid{background:rgba(255,255,255,0.98);border:1px solid rgba(62,124,140,0.16);border-radius:16px;padding:22px;box-shadow:0 20px 48px rgba(62,124,140,0.12);}
    .wh{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;flex-wrap:wrap;gap:12px;}
    .wt{font-size:14px;font-weight:700;color:#111827;}
    .filter-bar{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
    .filter-input{padding:9px 14px;border-radius:10px;border:1px solid #312E81;background:#1A0B4D;color:#FFFFFF;font-family:'Sora','Outfit',sans-serif;font-size:13px;outline:none;min-width:220px;}
    .filter-input:focus{border-color:#6D28D9;box-shadow:0 0 0 3px rgba(109,40,217,0.2);}
    .filter-select{padding:9px 14px;border-radius:10px;border:1px solid #312E81;background:#1A0B4D;color:#FFFFFF;font-family:'Sora','Outfit',sans-serif;font-size:13px;cursor:pointer;outline:none;}
    .filter-select:focus{border-color:#6D28D9;box-shadow:0 0 0 3px rgba(109,40,217,0.2);}
    .enq-cs-trigger{width:100%;text-align:left;cursor:pointer;padding:9px 12px;border-radius:10px;border:1px solid rgba(62,124,140,0.28);background:#FFFFFF;color:#102a33;font-family:'Sora','Outfit',sans-serif;font-size:13px;font-weight:700;display:flex;justify-content:space-between;align-items:center;box-sizing:border-box;box-shadow:0 1px 2px rgba(15,23,42,0.04);}
    .enq-cs-trigger:hover{border-color:#3E7C8C;box-shadow:0 0 0 3px rgba(62,124,140,0.14);}
    .enq-cs-trigger:disabled{opacity:.62;cursor:not-allowed;background:#f1f5f9;color:#94a3b8;}
    .enq-cs-filter-trigger{width:auto;min-width:170px;padding:9px 14px;border-radius:10px;background:#FFFFFF;color:#102a33;}


    .tbl-scroll-wrap{overflow:auto;max-height:520px;border-radius:10px;border:1px solid rgba(62,124,140,0.16);background:#ffffff;isolation:isolate;scrollbar-width:thin;scrollbar-color:rgba(62,124,140,0.28) transparent;}
    .tbl-scroll-wrap::-webkit-scrollbar{ width:6px; height:6px; }
    .tbl-scroll-wrap::-webkit-scrollbar-track{ background:transparent; }
    .tbl-scroll-wrap::-webkit-scrollbar-thumb{ background:rgba(62,124,140,0.28); border-radius:99px; }
    .tbl-scroll-wrap::-webkit-scrollbar-corner{ background:transparent; }

    .tbl{
      width:100%;
      border-collapse:separate;
      border-spacing:0;
      table-layout:auto;
    }
    .tbl thead{ position:sticky; top:0; z-index:10; }
    .tbl thead tr{ background:#f8fafc; }
    .tbl th{
      position:sticky; top:0; z-index:3;
      text-align:left; font-size:10px; font-weight:700;
      color:#2d5f6e; text-transform:uppercase; letter-spacing:.6px; padding:12px 14px; background:#f8fafc; border-bottom:1px solid rgba(62,124,140,0.16); white-space:nowrap;
    }
    .tbl td{padding:13px 14px;font-size:13px;border-bottom:1px solid rgba(129,140,248,0.12);color:#111827;vertical-align:middle;white-space:nowrap;}
    .tbl tbody tr:last-child td{ border-bottom:none; } .tbl tbody tr:hover{background:rgba(62,124,140,0.06);}

    .th-actions{ position:sticky !important; right:0 !important; top:0 !important; z-index:5 !important; text-align:center !important; }
    .td-actions{ position:sticky !important; right:0 !important; z-index:2 !important; text-align:center !important; vertical-align:middle !important; padding:0 14px !important; }

    .enq-id{color:#2d5f6e;font-weight:700;font-size:12px;font-family:monospace;}
    .enq-name{font-weight:700;color:#111827;font-size:13px;}
    .enq-inst{font-size:11px;color:#64748b;margin-top:2px;}
    .bdg{display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;white-space:nowrap;}
    .bdg::before{content:'';width:5px;height:5px;border-radius:50%;background:currentColor;flex-shrink:0;}
    .bdg-g{background:rgba(52,211,153,0.08);color:#059669;border:1px solid rgba(52,211,153,0.18);}
    .bdg-b{background:rgba(14,165,233,0.08);color:#0e7490;border:1px solid rgba(14,165,233,0.18);}
    .bdg-y{background:rgba(245,158,11,0.10);color:#d97706;border:1px solid rgba(245,158,11,0.18);}
    .board-tag{font-size:11px;font-weight:700;padding:3px 9px;border-radius:6px;background:rgba(14,165,233,.12);color:#0ea5e9;border:1px solid rgba(14,165,233,.2);white-space:nowrap;}
    .empty{text-align:center;padding:40px;color:#6b7280;font-size:14px;}
    .loading{display:flex;justify-content:center;padding:40px;}
    .inf-spinner{width:26px;height:26px;border:3px solid rgba(14,165,233,.2);border-top-color:#0ea5e9;border-radius:50%;animation:spin .7s linear infinite;}
    @keyframes spin{to{transform:rotate(360deg);}}

    .act-menu{display:flex;align-items:center;justify-content:center;width:100%;height:100%;}
    .act-menu-btn{width:34px;height:34px;border-radius:10px;border:1px solid rgba(62,124,140,0.18);background:rgba(62,124,140,0.08);color:#2d5f6e;font-size:22px;line-height:1; cursor:pointer;transition:color .15s, background .15s, border-color .15s;
      display:flex;align-items:center;justify-content:center; padding:0;margin:auto;
    }
    .act-menu-btn:hover{color:#2d5f6e;background:rgba(242,140,40,0.18);border-color:rgba(242,140,40,0.26);box-shadow:0 8px 20px rgba(242,140,40,0.18);}

    .act-menu-dd-portal .act-menu-item{
      width:100%;display:flex;align-items:center;gap:10px;
      padding:9px 10px;border:none;background:transparent;
      color:#C4B5FD;font-size:13px;font-weight:600;
      border-radius:8px;cursor:pointer;transition:background .12s;text-align:left;
      font-family:'Sora','Outfit',sans-serif;
    }
    .act-menu-dd-portal .act-menu-item:hover{ background:rgba(14,165,233,0.10); }
    .act-menu-icon{width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;color:#A855F7;}
    .act-menu-delete{color:#b91c1c !important;}
    .act-menu-delete .act-menu-icon{color:#b91c1c !important;}

    .modal-box{background:#20105E;border:1px solid #312E81;border-radius:20px;width:100%;max-width:560px;max-height:90vh;overflow-y:auto;padding:28px 28px 24px;box-shadow:0 24px 64px rgba(109,40,217,0.2);}
    .modal-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
    .modal-title{font-size:16px;font-weight:800;color:#FFFFFF;display:flex;align-items:center;gap:8px;}
    .modal-close{background:rgba(109,40,217,0.2);border:none;color:#C4B5FD;cursor:pointer;width:32px;height:32px;border-radius:8px;font-size:18px;display:flex;align-items:center;justify-content:center;transition:all .15s;}
    .modal-close:hover{background:rgba(109,40,217,0.3);color:#FFFFFF;}
    .detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
    .detail-full{grid-column:1/-1;}
    .detail-item{background:#20105E;border-radius:10px;padding:13px 16px;border:1px solid #312E81;}
    .detail-label{font-size:10px;font-weight:700;color:#C4B5FD;text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px;}
    .detail-val{font-size:14px;font-weight:600;color:#FFFFFF;}
    .msg-box{padding:10px 14px;border-radius:8px;font-size:13px;font-weight:600;margin-bottom:12px;}
    .msg-s{background:rgba(16,185,129,.15);color:#86EFAC;border:1px solid rgba(16,185,129,.3);}
    .msg-e{background:rgba(239,68,68,.15);color:#FCA5A5;border:1px solid rgba(239,68,68,.3);}
    .modal-footer{display:flex;gap:10px;justify-content:flex-end;margin-top:16px;}
    .btn{padding:10px 22px;border-radius:10px;font-family:'Sora','Outfit',sans-serif;font-size:13px;font-weight:700;cursor:pointer;border:none;transition:all .15s;}
    .btn-ghost{background:transparent;border:1px solid #312E81;color:#C4B5FD;}
    .btn-ghost:hover{background:rgba(109,40,217,0.15);}
    .btn-danger{background:rgba(239,68,68,.15);color:#FCA5A5;border:1px solid rgba(239,68,68,.3);}
    .btn-danger:hover{background:rgba(239,68,68,.25);}
    .status-select{width:100%;padding:9px 12px;border-radius:8px;border:1px solid #312E81;background:rgba(26,16,69,0.8);color:var(--text-primary);font-family:'Sora','Outfit',sans-serif;font-size:13px;cursor:pointer;outline:none;}
    .status-select:focus{border-color:#0ea5e9;box-shadow:0 0 0 3px rgba(14,165,233,0.18);}
  `;

  return (
    <AdminLayout>
      <style>{css}</style>
      <div className="page-shell">
        <div className="page-stack">

          {/* Page Header */}
          <div className="page-header-row">
            <div className="page-header-main">
              <div className="page-header-title-row">
                <div className="page-header-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="enquiries-title ptitle" style={{ fontSize: '1.6rem', fontWeight: 800, color: '#102a33', margin: '0 0 2px', letterSpacing: '-0.2px' }}>Enquiries</h1>
                  <div className="header-subtitle">Quote requests submitted from the website</div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <StatCards
            cards={statsData.map(s => ({
              title: s.lbl,
              value: s.val,
              description: s.desc,
              icon: s.icon,
              accent: s.color,
              active: statusFilter === s.activeFilter,
              onClick: () => handleStatCardClick(s.lbl),
            }))}
          />

          {/* Table Widget */}
          <div className="wid">
            <div className="wh">
              <span className="wt">All Enquiries ({filtered.length})</span>
              <div className="filter-bar">
                <input
                  className="filter-input"
                  placeholder="ðŸ” Search by name, institution, email, phone..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <CustomSelect
                  triggerClassName="enq-cs-filter-trigger"
                  value={statusFilter}
                  onChange={val => { setStatusFilter(val); setSortBy('newest'); }}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'new', label: 'New' },
                    { value: 'contacted', label: 'Contacted' },
                    { value: 'closed', label: 'Closed' },
                  ]}
                />
                <CustomSelect
                  triggerClassName="enq-cs-filter-trigger"
                  value={sortBy}
                  onChange={setSortBy}
                  options={[
                    { value: 'newest', label: 'Newest First' },
                    { value: 'oldest', label: 'Oldest First' },
                  ]}
                />
              </div>
            </div>

            {loading ? (
              <div className="loading"><div className="inf-spinner" /></div>
            ) : (
              <>
                <div className="tbl-scroll-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 36, background: TH_BG }}>#</th>
                        <th style={{ minWidth: 170, background: TH_BG }}>Name / Institution</th>
                        <th style={{ minWidth: 150, background: TH_BG }}>Contact</th>
                        <th style={{ minWidth: 130, background: TH_BG }}>Board Type</th>
                        <th style={{ minWidth: 110, background: TH_BG }}>Status</th>
                        <th style={{ minWidth: 100, background: TH_BG }}>Date</th>
                        <th
                          className="th-actions"
                          style={{ minWidth: 80, background: TH_BG, boxShadow: STICKY_COL_SHADOW, borderLeft: '1px solid rgba(14,165,233,0.18)' }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="empty" style={{ background: ROW_ODD_BG }}>
                            No enquiries found.
                          </td>
                        </tr>
                      ) : (
                        pageItems.map((e, idx) => {
                          const sm = getStatus(e.status);
                          const rowBg = getRowBg(idx, e._id);

                          return (
                            <tr
                              key={e._id}
                              onClick={() => setSelEnquiry(e)}
                              onMouseEnter={() => setHoveredRow(e._id)}
                              onMouseLeave={() => setHoveredRow(null)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td style={{ color: '#ffffff', fontSize: 12, background: rowBg }}>{pageStart + idx + 1}</td>
                              <td style={{ background: rowBg }}>
                                <div className="enq-name">{e.name}</div>
                                <div className="enq-inst">{e.institutionName}</div>
                              </td>
                              <td style={{ background: rowBg }}>
                                <div style={{ fontSize: 12, color: '#FFFFFF' }}>{e.phone}</div>
                                <div style={{ fontSize: 11, color: '#C4B5FD', marginTop: 2 }}>{e.email}</div>
                              </td>
                              <td style={{ background: rowBg }}>
                                <span className="board-tag">{e.boardType}</span>
                              </td>
                              <td style={{ background: rowBg }}>
                                <span className={'bdg ' + sm.cls}>{sm.label}</span>
                              </td>
                              <td style={{ color: '#94a3b8', fontSize: 12, background: rowBg }}>
                                {fmtDate(e.createdAt)}
                              </td>
                              <td
                                className="td-actions"
                                style={{ background: rowBg, boxShadow: STICKY_COL_SHADOW, borderLeft: '1px solid rgba(14,165,233,0.10)' }}
                                onClick={ev => ev.stopPropagation()}
                              >
                                <div className="act-menu">
                                  <button
                                    type="button"
                                    className="act-menu-btn"
                                    title="Actions"
                                    onClick={ev => handleMenuOpen(ev, e._id)}
                                  >
                                    â‹®
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {openMenuId && (() => {
                  const enquiry = pageItems.find(e => e._id === openMenuId);
                  if (!enquiry) return null;
                  return (
                    <PortaledDropdown
                      top={menuPos.top}
                      right={menuPos.right}
                      onClose={() => setOpenMenuId(null)}
                      onView={() => setSelEnquiry(enquiry)}
                      onDelete={() => openDelete(enquiry)}
                    />
                  );
                })()}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={filtered.length}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* â”€â”€ View / Update Modal â”€â”€ */}
      {selEnquiry && (
        <PortalModal onBackdropClick={() => setSelEnquiry(null)}>
          <div className="modal-box">
            <div className="modal-hdr">
              <span className="modal-title">ðŸ“‹ Enquiry Details</span>
              <button className="modal-close" onClick={() => setSelEnquiry(null)}>âœ•</button>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-label">Name</div>
                <div className="detail-val">{selEnquiry.name}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Institution</div>
                <div className="detail-val">{selEnquiry.institutionName}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Phone</div>
                <div className="detail-val">{selEnquiry.phone}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Email</div>
                <div className="detail-val" style={{ fontSize: 13 }}>{selEnquiry.email}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Board Type</div>
                <div style={{ marginTop: 4 }}><span className="board-tag">{selEnquiry.boardType}</span></div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Submitted On</div>
                <div className="detail-val" style={{ fontSize: 13 }}>{fmtDateTime(selEnquiry.createdAt)}</div>
              </div>
              {selEnquiry.message && (
                <div className="detail-item detail-full">
                  <div className="detail-label">Message</div>
                  <div className="detail-val" style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 500, whiteSpace: 'normal' }}>{selEnquiry.message}</div>
                </div>
              )}
              <div className="detail-item detail-full">
                <div className="detail-label">Update Status</div>
                <div style={{ marginTop: 6 }}>
                  <CustomSelect
                    value={normalizeStatus(selEnquiry.status)}
                    onChange={val => handleStatusChange(selEnquiry._id, val)}
                    options={[
                      { value: 'new', label: 'New' },
                      { value: 'contacted', label: 'Contacted' },
                      { value: 'closed', label: 'Closed' },
                    ]}
                  />
                </div>
              </div>
            </div>
            {statusMsg && <div className={'msg-box ' + (statusMsg.startsWith('âœ…') ? 'msg-s' : 'msg-e')}>{statusMsg}</div>}
            <div className="modal-footer">
              <button className="btn btn-danger" onClick={() => { setSelEnquiry(null); openDelete(selEnquiry); }}>ðŸ—‘ï¸ Delete</button>
              <button className="btn btn-ghost" onClick={() => setSelEnquiry(null)}>Close</button>
            </div>
          </div>
        </PortalModal>
      )}

      {/* â”€â”€ Delete Confirm Modal â”€â”€ */}
      {deleteConfirm && (
        <DeleteConfirmModal
          itemName={deleteConfirm.label}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </AdminLayout>
  );
};

export default Enquiries;


